// Titan's haze as Cassini saw it: a volume of scattering aerosol over the
// opaque deck that the surface mesh draws (src/data's titan map, the haze's
// own measured colour), so the disc keeps a soft edge, the limb turns from
// gold to blue as the small particles high up take over, sunlight carries
// past the terminator through the upper haze, and the detached haze layer
// stands apart from the main haze as a thin blue shell. Backlit, the whole
// ring of air lights up: orange where the light has crossed deep haze, blue
// at the top.
//
// The sources for the numbers:
// - Detached layer at 500 km (Cassini, 2005; it sank to 380 km by 2010):
//   Seignovert et al. (2017), "The seasonal cycle of Titan's detached haze",
//   arXiv:1804.10842; NASA/JPL PIA06090 ("near 500 kilometers").
// - Small particles high up scatter blue, larger ones lower down orange:
//   PIA21625 (backlit, ISS) and PIA17180 (blue halo against the night side).
// - Forward-scattering aggregates: a two-term Henyey-Greenstein lobe.
// - Scale height, optical depth and the layer's strength are fitted by eye
//   and by profile to Cassini's natural-colour frames: PIA14602's limb (in
//   display values the disc holds 0.89 of its centre at 0.7 radii, fades
//   over the last tenth, and its blue fringe runs out to about 1.15), the
//   terminator of PIA06230 and the backlit rings of PIA21625 and PIA17180.
// - The albedo is an effective one: golden low down, where the light has
//   been scattered many times, near neutral high up, where it scatters once.
//
// Everything is worked in the moon's frame in units of its drawn radius, in
// view space, so it holds its precision at true scale, 47,000 units from the
// Sun (a float32 world position there is good to about 0.004, 5% of Titan).
// The ray is sampled evenly in altitude on each side of its closest approach,
// so the 50 km detached layer is never stepped over, however the ray runs.

export const titanHazeVertexShader = /* glsl */ `
  uniform float uBodyLocalR;
  varying vec3 vViewPos;
  varying vec3 vCentre;
  varying float vBodyR;
  void main() {
    vec4 mv = modelViewMatrix * vec4(position, 1.0);
    vViewPos = mv.xyz;
    vCentre = (modelViewMatrix * vec4(0.0, 0.0, 0.0, 1.0)).xyz;
    vBodyR = uBodyLocalR * length(modelViewMatrix[0].xyz);
    gl_Position = projectionMatrix * mv;
  }
`;

export const titanHazeFragmentShader = /* glsl */ `
  uniform vec3 uSunDirView;   // from Titan's centre to the Sun, view space
  uniform float uSunE;        // the sunlight's scale, as the deck receives it
  uniform float uTopR;        // the shell's top, in body radii
  uniform float uH;           // main haze scale height, body radii
  uniform float uB0;          // main haze extinction at the deck, 550 nm, per body radius
  uniform float uDetH;        // detached layer altitude, body radii
  uniform float uDetW;        // its Gaussian width (sigma), body radii
  uniform float uDetB;        // its peak extinction, 550 nm, per body radius
  uniform float uOpacity;     // 0..1 fade (the focus fly-in, hidden layers)
  uniform vec3 uMainAlb;      // main haze albedo deep down (multiple scattering folded in)
  uniform vec3 uThinAlb;      // and high up, where it scatters once
  uniform vec2 uThinRange;    // altitudes (body radii) of the change from one to the other
  uniform float uG;           // forward lobe of the main haze's phase function
  varying vec3 vViewPos;
  varying vec3 vCentre;
  varying float vBodyR;

  const float PI = 3.14159265;
  // Extinction by wavelength (R 620, G 550, B 460 nm): the main haze with an
  // Angstrom exponent of 2, the detached layer's small grains near Rayleigh.
  const vec3 MAIN_SPEC = vec3(0.79, 1.0, 1.43);
  const vec3 DET_SPEC = vec3(0.62, 1.0, 2.04);
  // The detached layer's grains barely absorb.
  const vec3 DET_ALB = vec3(0.96, 0.96, 0.94);
  const int STEPS = 20;

  float hg(float c, float g) {
    float g2 = g * g;
    return (1.0 - g2) / (4.0 * PI * pow(max(1.0 + g2 - 2.0 * g * c, 1e-4), 1.5));
  }

  vec3 betaMain(float h) { return uB0 * MAIN_SPEC * exp(-h / uH); }
  vec3 betaDet(float h) {
    float x = (h - uDetH) / uDetW;
    return uDetB * DET_SPEC * exp(-0.5 * x * x);
  }

  // The main haze's column from radius r toward the Sun, in units of the
  // local extinction times the scale height (the Chapman function), with the
  // usual closed form above the horizon and the tangent-path identity below.
  // Returns -1 when the deck itself hides the Sun.
  float chapmanCol(float r, float cosChi) {
    float X = r / uH;
    float c = sqrt(PI * X * 0.5);
    if (cosChi >= 0.0) return c / ((c - 1.0) * cosChi + 1.0);
    float s = sqrt(max(1.0 - cosChi * cosChi, 0.0));
    if (r * s < 1.0) return -1.0;
    float cs = sqrt(PI * X * s * 0.5);
    float up = c / ((c - 1.0) * (-cosChi) + 1.0);
    return 2.0 * cs * exp(min(X * (1.0 - s), 60.0)) - up;
  }

  void main() {
    float R = max(vBodyR, 1e-12);
    vec3 ro = -vCentre / R;               // camera, from Titan's centre
    vec3 rd = normalize(vViewPos);
    float b = dot(ro, rd);
    float c0 = dot(ro, ro);
    float discT = b * b - (c0 - uTopR * uTopR);
    if (discT <= 0.0) discard;
    float sq = sqrt(discT);
    float tEnter = max(-b - sq, 0.0);
    float tExit = -b + sq;
    if (tExit <= 0.0) discard;
    float discB = b * b - (c0 - 1.0);
    bool hitsBody = false;
    float tEnd = tExit;
    if (discB > 0.0) {
      float tb = -b - sqrt(discB);
      if (tb > 0.0) { hitsBody = true; tEnd = tb; }
    }
    float tc = -b;                         // closest approach
    float d2 = max(c0 - b * b, 0.0);       // its squared distance from the centre

    vec3 sunDir = normalize(uSunDirView);
    float cosTheta = dot(rd, sunDir);      // scattering: 1 looking at the Sun
    float pMain = 0.85 * hg(cosTheta, uG) + 0.15 * hg(cosTheta, -0.3);
    float pDet = 0.85 * hg(cosTheta, 0.6) + 0.15 * (3.0 / (16.0 * PI)) * (1.0 + cosTheta * cosTheta);

    vec3 inscatter = vec3(0.0);
    vec3 trans = vec3(1.0);
    // A ray that just clears the deck would add its far half, which a ray
    // that just hits it cannot: a step of a few per cent round the disc.
    // That far half lies behind the densest haze, so it is faded in over
    // the lowest two scale heights.
    float farWeight = smoothstep(1.0, 1.0 + 2.0 * uH, sqrt(d2));
    // Two branches, each monotonic in radius: down to the closest approach
    // (or the deck) and back up. Sampled evenly in radius.
    for (int branch = 0; branch < 2; branch++) {
      float ta, tb2;
      if (branch == 0) { ta = tEnter; tb2 = min(tEnd, max(tc, tEnter)); }
      else { ta = max(tc, tEnter); tb2 = tEnd; }
      if (tb2 - ta <= 1e-7) continue;
      vec3 pa = ro + rd * ta;
      vec3 pb = ro + rd * tb2;
      float ra = length(pa);
      float rb = length(pb);
      float side = branch == 0 ? -1.0 : 1.0;
      float tPrev = ta;
      for (int i = 1; i <= STEPS; i++) {
        float f = float(i) / float(STEPS);
        float r = mix(ra, rb, f);
        float tNow = i == STEPS ? tb2 : tc + side * sqrt(max(r * r - d2, 0.0));
        float dt = max(tNow - tPrev, 0.0);
        float tm = 0.5 * (tPrev + tNow);
        tPrev = tNow;
        if (dt <= 0.0) continue;
        vec3 p = ro + rd * tm;
        float rm = length(p);
        float h = rm - 1.0;
        vec3 bm = betaMain(h);
        vec3 bd = betaDet(h);
        vec3 ext = bm + bd;
        float cosChi = dot(p / rm, sunDir);
        float col = chapmanCol(rm, cosChi);
        vec3 sunT = col < 0.0 ? vec3(0.0) : exp(-bm * uH * col);
        vec3 alb = mix(uMainAlb, uThinAlb, smoothstep(uThinRange.x, uThinRange.y, h));
        vec3 scat = (bm * alb * pMain + bd * DET_ALB * pDet) * sunT * uSunE;
        vec3 segT = exp(-ext * dt);
        float w = branch == 1 ? farWeight : 1.0;
        inscatter += w * trans * scat * (1.0 - segT) / max(ext, vec3(1e-6));
        trans *= segT;
      }
    }
    // The deck is drawn lit by the full Sun; the haze above it takes its
    // share of that light on the way in as well as on the way out, which
    // darkens it towards the terminator and the limb as Titan's disc does.
    if (hitsBody) {
      vec3 pd = ro + rd * tEnd;
      float colD = chapmanCol(length(pd), dot(normalize(pd), sunDir));
      trans *= colD < 0.0 ? vec3(0.0) : exp(-betaMain(0.0) * uH * colD);
    }
    float a = uOpacity;
    // Premultiplied over: colour + what lies behind times the transmittance.
    gl_FragColor = vec4(inscatter * a, mix(1.0, dot(trans, vec3(0.2126, 0.7152, 0.0722)), a));
  }
`;
